import type { ChartTooltipProps, TooltipContentItem } from '@/types/chart'
import type { ReactNode } from 'react'

export interface ComposedChartConfig<T extends object> {
  xFormatter: (item: T, index: number, all: T[]) => string
  lineKey?: keyof T
  barKey?: keyof T

  hideLine?: boolean
  hideBar?: boolean
  hideXAxis?: boolean
  hideYAxis?: boolean
  hideGrid?: boolean

  tooltip: ChartTooltipProps<T>
}

export const createComposedLineBarConfig = <T extends object>({
  lineKey,
  barKey,
  xFormatter,
  hideLine,
  hideBar,
  hideXAxis = false,
  hideYAxis = false,
  hideGrid = false,
  tooltipHeaderFormatter,
  tooltipLabelFormatter,
  lineValueFormatter,
  barValueFormatter,
  lineLabel,
  barLabel,
  tooltip,
  ...restConfig
}: {
  lineKey?: keyof T
  barKey?: keyof T
  xFormatter: (item: T, index: number, all: T[]) => string
  hideLine?: boolean
  hideBar?: boolean
  hideXAxis?: boolean
  hideYAxis?: boolean
  hideGrid?: boolean
  tooltipHeaderFormatter: (data: T) => string
  tooltipLabelFormatter?: (label: string) => ReactNode
  lineValueFormatter?: (value: T[keyof T]) => string
  barValueFormatter?: (value: T[keyof T]) => string
  lineLabel?: string
  barLabel?: string
  tooltip?: Partial<ChartTooltipProps<T>>
}): ComposedChartConfig<T> => {
  const capitalize = (str: string): string => str.charAt(0).toUpperCase() + str.slice(1)

  const finalHideLine = hideLine ?? !lineKey
  const finalHideBar = hideBar ?? !barKey

  const finalLineLabel = lineLabel ?? (lineKey ? capitalize(String(lineKey)) : '')
  const finalBarLabel = barLabel ?? (barKey ? capitalize(String(barKey)) : '')

  const tooltipItems: TooltipContentItem<T>[] = tooltip?.items ?? []

  if (tooltipItems.length === 0) {
    if (barKey) {
      tooltipItems.push({
        label: finalBarLabel,
        valueKey: barKey,
        renderLabel: tooltipLabelFormatter ?? ((label) => label),
        renderValue: barValueFormatter ?? ((value) => String(value)),
      })
    }

    if (lineKey) {
      tooltipItems.push({
        label: finalLineLabel,
        valueKey: lineKey,
        renderLabel: tooltipLabelFormatter ?? ((label) => label),
        renderValue: lineValueFormatter ?? ((value) => String(value)),
      })
    }
  }

  const finalTooltip: ChartTooltipProps<T> = {
    renderHeader: tooltipHeaderFormatter ?? tooltip?.renderHeader ?? (() => ''),
    headerClassName: tooltip?.headerClassName,
    items: tooltipItems,
  }

  return {
    xFormatter,
    lineKey,
    barKey,
    hideLine: finalHideLine,
    hideBar: finalHideBar,
    hideXAxis,
    hideYAxis,
    hideGrid,
    tooltip: finalTooltip,
    ...restConfig,
  }
}
